import { DEFAULT_BEHAVIOR } from '../../constants/defaultBehavior';
import { BehaviorResponseBody } from '../../types/Api';
import type { EagGridState } from '../createEagGridStore';
import { useEagGridSelector } from '../useEagGridSelector';

/**
 * Module-level selector functions for behavior flags.
 *
 * Each selector returns a primitive value (boolean, string, enum) so
 * useSyncExternalStore can compare with Object.is() — stable references
 * when the underlying definition.behavior hasn't changed.
 *
 * All selectors fall back to DEFAULT_BEHAVIOR when definition is not loaded.
 */

export const selectBehavior = (state: EagGridState): BehaviorResponseBody => state.definition?.behavior ?? DEFAULT_BEHAVIOR;
export const selectSmartSearchEnabled = (state: EagGridState): boolean => selectBehavior(state).smartSearchEnabled;
export const selectPresetsEnabled = (state: EagGridState): boolean => selectBehavior(state).presetsEnabled;
export const selectGlobalActionsEnabled = (state: EagGridState): boolean => selectBehavior(state).globalActionsEnabled;
export const selectFilterToolPanelVisible = (state: EagGridState): boolean => selectBehavior(state).filterToolPanelVisible;
export const selectColumnToolPanelVisible = (state: EagGridState): boolean => selectBehavior(state).columnToolPanelVisible;
export const selectSettingsToolPanelVisible = (state: EagGridState): boolean => selectBehavior(state).settingsToolPanelVisible;
export const selectColumnMovingAllowed = (state: EagGridState): boolean => selectBehavior(state).columnMovingAllowed;
export const selectColumnResizingAllowed = (state: EagGridState): boolean => selectBehavior(state).columnResizingAllowed;
export const selectColumnSortMode = (state: EagGridState): 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE' => selectBehavior(state).columnSortMode;
export const selectColumnMenuEnabled = (state: EagGridState): boolean => selectBehavior(state).columnMenuEnabled;
export const selectColumnPinningAllowed = (state: EagGridState): boolean => selectBehavior(state).columnPinningAllowed;
export const selectColumnAutoSizingAllowed = (state: EagGridState): boolean => selectBehavior(state).columnAutoSizingAllowed;
export const selectColumnHidingAllowed = (state: EagGridState): boolean => selectBehavior(state).columnHidingAllowed;
export const selectActionColumnEnabled = (state: EagGridState): boolean => selectBehavior(state).actionColumnEnabled;
export const selectPaginationEnabled = (state: EagGridState): boolean => selectBehavior(state).paginationEnabled;
export const selectRowSelectMode = (state: EagGridState): 'SINGLE' | 'MULTIPLE' | 'NONE' => selectBehavior(state).rowSelectMode;
export const selectWrapperStyle = (state: EagGridState): 'CARD' | 'INLINE' => selectBehavior(state).wrapperStyle;
export const selectFilterMode = (state: EagGridState): 'NATIVE' | 'CUSTOM' => selectBehavior(state).filterMode;
export const selectClipboardExportAllowed = (state: EagGridState): boolean => selectBehavior(state).clipboardExportAllowed;

/**
 * Hook wrappers for component use.
 *
 * These hooks subscribe the component to the specific behavior flag via
 * useEagGridSelector + useSyncExternalStore. The component will re-render
 * only when that specific flag changes.
 */

export function useSmartSearchEnabled() {
  return useEagGridSelector(selectSmartSearchEnabled);
}
export function usePresetsEnabled() {
  return useEagGridSelector(selectPresetsEnabled);
}
export function useGlobalActionsEnabled() {
  return useEagGridSelector(selectGlobalActionsEnabled);
}
export function useFilterToolPanelVisible() {
  return useEagGridSelector(selectFilterToolPanelVisible);
}
export function useColumnToolPanelVisible() {
  return useEagGridSelector(selectColumnToolPanelVisible);
}
export function useSettingsToolPanelVisible() {
  return useEagGridSelector(selectSettingsToolPanelVisible);
}
export function useColumnMovingAllowed() {
  return useEagGridSelector(selectColumnMovingAllowed);
}
export function useColumnResizingAllowed() {
  return useEagGridSelector(selectColumnResizingAllowed);
}
export function useColumnSortMode() {
  return useEagGridSelector(selectColumnSortMode);
}
export function useColumnMenuEnabled() {
  return useEagGridSelector(selectColumnMenuEnabled);
}
export function useColumnPinningAllowed() {
  return useEagGridSelector(selectColumnPinningAllowed);
}
export function useColumnAutoSizingAllowed() {
  return useEagGridSelector(selectColumnAutoSizingAllowed);
}
export function useColumnHidingAllowed() {
  return useEagGridSelector(selectColumnHidingAllowed);
}
export function useActionColumnEnabled() {
  return useEagGridSelector(selectActionColumnEnabled);
}
export function usePaginationEnabled() {
  return useEagGridSelector(selectPaginationEnabled);
}
export function useClipboardExportAllowed() {
  return useEagGridSelector(selectClipboardExportAllowed);
}